/** Profile → Security: every user's own passkeys/MFA, sessions, devices, logins. */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { own } from "@/lib/terminology";

const fmt = (iso) => (iso || "").replace("T", " ").slice(0, 16);

export default function AccountSecurity() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    api.get("/security-centre/me/security").then(({ data }) => setD(data));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const revokeSession = async (s) => {
    try { await api.post(`/security-centre/me/sessions/${s.session_ref}/revoke`);
      toast.success("Session logged out"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const revokeDevice = async (dev) => {
    if (!window.confirm(`Revoke ${dev.name}? All its sessions will be logged out.`)) return;
    try { await api.post(`/security-centre/me/devices/${dev.id}/revoke`);
      toast.success("Device revoked"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const Card = ({ title, children }) => (
    <div className="border border-slate-200 rounded-xl bg-white p-4">
      <div className="text-[11px] font-heading font-semibold mb-2">{title}</div>{children}</div>);
  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="account-security-page">
      <header className="flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-emerald-700" />
        <div className="text-lg font-heading font-semibold">{own(user, "security")}</div>
      </header>
      <Card title="Authentication">
        <div className="text-[11.5px]">Methods: <b>{d.auth_methods.join(" + ")}</b>
          {!d.mfa_enabled && <Link to="/account/mfa" data-testid="asec-add-mfa"
              className="ml-2 text-[10px] px-2 py-1 rounded bg-slate-900 text-white">Add passkey / MFA</Link>}
          {d.mfa_enabled && <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold">MFA ACTIVE</span>}
        </div>
      </Card>
      <Card title="Active sessions">
        <div className="space-y-1.5" data-testid="asec-sessions">
          {d.sessions.map((s) => (
            <div key={s.session_ref} className="flex items-center gap-2 text-[11px] border border-slate-100 rounded-lg px-3 py-1.5">
              <span className="flex-1">{s.browser} / {s.platform} · {s.ip}
                {s.current && <span className="ml-1 text-[8.5px] px-1 rounded bg-emerald-100 text-emerald-800">THIS SESSION</span>}</span>
              <span className="text-slate-400">{fmt(s.last_seen)}</span>
              {!s.current && <button onClick={() => revokeSession(s)}
                  data-testid={`asec-revoke-${s.session_ref}`}
                  className="text-[9.5px] px-1.5 py-0.5 rounded border border-rose-300 text-rose-700">Log out</button>}
            </div>))}
        </div>
      </Card>
      <Card title="Trusted devices">
        <div className="space-y-1.5" data-testid="asec-devices">
          {d.devices.map((dev) => (
            <div key={dev.id} className="flex items-center gap-2 text-[11px] border border-slate-100 rounded-lg px-3 py-1.5">
              <span className="flex-1">{dev.name} · <span className="text-slate-400">{dev.status}</span></span>
              <span className="text-slate-400">last {fmt(dev.last_seen)}</span>
              <button onClick={() => revokeDevice(dev)} data-testid={`asec-revoke-dev-${dev.id}`}
                  className="text-[9.5px] px-1.5 py-0.5 rounded border border-rose-300 text-rose-700">Revoke</button>
            </div>))}
        </div>
      </Card>
      <Card title="Recent login activity">
        <div className="space-y-1 text-[10.5px] text-slate-600" data-testid="asec-logins">
          {d.recent_logins.map((l, i) => (
            <div key={i}>{fmt(l.at)} · {l.ip} · <span className="text-slate-400">{(l.user_agent || "").slice(0, 60)}</span></div>))}
        </div>
      </Card>
    </div>);
}
